import { ActivityStatusModel } from '../../../../../features/activities/api/useActivityStatusQuery.ts';
import { faCreditCard, faCreditCardAlt, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { OverrideMccButton } from '../../../../../features/activities/editActivity/editActivityHotelRates/components/overrideMccButton.tsx';
import { useMemo } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';

import { useCreateVccMutation } from '../../../../../features/activities/editActivity/editActivityHotelRates/api/useCreateVccMutation.ts';
import { useResendCorporatePaymentEmailMutation } from '../../../../../features/activities/editActivity/editActivityHotelRates/api/useResendCorporatePaymentEmailMutation.ts';
import { BaseConfirmPopover, BaseMenuItem, BaseToolbarButton, useStandardToast } from '@cks-internal/react-base';
import { useActivityQueryInvalidator } from '../../../../../features/activities/editActivity/useActivityQueryInvalidator.ts';
import { useAddOtherCardMutation } from '../../../../../features/activities/editActivity/editActivityHotelRates/api/useAddOtherCardMutation.ts';
import { HotelBookingType } from 'src/globalModels/common/hotelBookingType.ts';
import { EditActivityUpdateModel } from '../../../../../features/activities/editActivity/editActivityForm/schema/editActivitySchema';

type Props = {
  activityId: number;
  activityStatus: ActivityStatusModel;
  isDisabled: boolean;
  formMethods: UseFormReturn<Partial<EditActivityUpdateModel>>;
};

export const FormOfPaymentCreateButtons = (props: Props) => {
  const createVccMutation = useCreateVccMutation();
  const addOtherCardMutation = useAddOtherCardMutation();
  const resendCorporatePaymentEmailMutation = useResendCorporatePaymentEmailMutation();
  const toast = useStandardToast();
  const activityQueryInvalidator = useActivityQueryInvalidator(props.activityId!);

  const otherCardDisabledMessages = useMemo(() => {
    if (!props.activityStatus) {
      return ['Loading...'];
    }
    if (!props.activityStatus?.OtherCompanyPaymentCards || props.activityStatus.OtherCompanyPaymentCards.length == 0) {
      return ['No other cards found'];
    }

    if (!props.activityStatus.HasBookedHotel) {
      return ['Other payment cards are only available post-booking'];
    }

    return undefined;
  }, [props.activityStatus.OtherCompanyPaymentCards, props.activityStatus.HasBookedHotel]);

  const otherCardMenuItems: BaseMenuItem[] = useMemo(() => {
    if (!props.activityStatus.OtherCompanyPaymentCards) {
      return [];
    }
    return props.activityStatus.OtherCompanyPaymentCards.map((val) => {
      let description = `${val.CreditCardTypeName} ending ${val.CardNumberLastFour}`;
      if (val.Description) {
        description += ' - ' + val.Description;
      }
      return {
        text: description,
        onClick: async () => {
          await addOtherCardMutation.mutateAsync({ activityId: props.activityId, cardId: val.Id! });
          toast.successToast('Other Card Added', 'The backup card has been added to this booking.');
          await activityQueryInvalidator.invalidateAllQueriesForActivity();
          await activityQueryInvalidator.invalidateStatusQuery();
        },
        icon: faCreditCard
      };
    });
  }, [props.activityStatus.OtherCompanyPaymentCards]);

  const canOverrideMcc = useMemo(() => {
    const messages: string[] = [];

    if (!props.activityStatus.HasWexDirectPayment) {
      // Assuming 13 is the ID for Wex Direct
      messages.push('WEX Direct Payment Method required for MCC control.');
    }

    if (messages.length > 0) {
      return messages;
    }
    return undefined;
  }, [props.activityStatus]);

  const [companyId, checkInDate, checkOutDate] = useWatch({
    control: props.formMethods.control,
    name: ['CompanyId', 'CheckInDate', 'CheckOutDate']
  });

  const canCreatePaymentCard = useMemo(() => {
    const toReturn = [];
    if (props.activityStatus.HasFormOfPayment) {
      toReturn?.push('A form of payment already exists.');
    }
    if (props.isDisabled) {
      toReturn?.push('This activity is currently disabled.');
    }
    if (props.activityStatus.HotelBookingType == HotelBookingType.Personal) {
      toReturn?.push('Personal bookings do not support VCCs.');
    }
    if (!companyId) {
      toReturn?.push('A company must be selected.');
    }
    if (!checkInDate || !checkOutDate) {
      toReturn?.push('Travel dates must be selected.');
    }

    return toReturn;
  }, [
    props.activityStatus.HasFormOfPayment,
    props.isDisabled,
    props.activityStatus.HotelBookingType,
    companyId,
    checkInDate,
    checkOutDate
  ]);

  const onAddVcc = async () => {
    await createVccMutation.mutateAsync({ activityId: props.activityId });
    toast.successToast('Virtual WEX Card Added', 'A virtual WEX card has been added to this booking.');
    await activityQueryInvalidator.invalidateAllQueriesForActivity();
    await activityQueryInvalidator.invalidateStatusQuery();
  };

  const onResendCorporatePaymentEmail = async () => {
    await resendCorporatePaymentEmailMutation.mutateAsync({ activityId: props.activityId });
  };

  return (
    <>
      <BaseToolbarButton icon={faCreditCardAlt} menuItems={otherCardMenuItems} disabled={otherCardDisabledMessages}>
        Add Other Card
      </BaseToolbarButton>

      <BaseConfirmPopover text={'Add VCC Card to Activity?'} onConfirm={onAddVcc}>
        <BaseToolbarButton
          isLoading={createVccMutation.isPending}
          toolTip={'Adds a virtual WEX card to the booking and should be used for backup only.'}
          disabled={canCreatePaymentCard}
          icon={faCreditCardAlt}>
          Add VCC
        </BaseToolbarButton>
      </BaseConfirmPopover>

      <OverrideMccButton
        activityId={props.activityId!}
        headerText={'Override with default MCC?'}
        buttonText={'Override MCC'}
        icon={faCreditCardAlt}
        iconColor={'primary.500'}
        disabled={canOverrideMcc}
      />

      <BaseConfirmPopover
        text={'Resend corporate card payment email to the hotel?'}
        onConfirm={onResendCorporatePaymentEmail}>
        <BaseToolbarButton
          isLoading={resendCorporatePaymentEmailMutation.isPending}
          toolTip={'Resend the corporate card payment email for this activity'}
          disabled={
            !props.activityStatus.HasCorporateCardPayment
              ? ['Corporate card payment is required']
              : !props.activityStatus.HasSentItinerary
                ? ['Itinerary has not been sent']
                : undefined
          }
          icon={faEnvelope}>
          Email Card
        </BaseToolbarButton>
      </BaseConfirmPopover>
    </>
  );
};
